import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AuthorHeader } from '@/components/AuthorHeader';
import { CoverImage } from '@/components/CoverImage';
import { CheerButton } from '@/components/CheerButton';
import { CommentButton } from '@/components/CommentButton';
import { ExternalShareButton } from '@/components/ExternalShareButton';
import { ActivityCommentThread } from '@/components/ActivityCommentThread';
import { useActivityCommentCount } from '@/hooks/useActivityComments';
import { formatDistanceToNow } from 'date-fns';
import { 
  BookOpen, 
  Star, 
  Flame, 
  Trophy, 
  Target,
} from 'lucide-react';

interface ActivityCardProps {
  activity: {
    id: string;
    type: string;
    payload: any;
    created_at: string;
    book_title?: string;
    book_cover_url?: string;
    user_id: string;
    profiles?: {
      username?: string;
      name?: string;
      image?: string;
      is_private_name?: boolean;
    };
  };
}

export function ActivityCard({ activity }: ActivityCardProps) {
  const navigate = useNavigate();
  const [commentThreadOpen, setCommentThreadOpen] = useState(false);
  const commentCount = useActivityCommentCount(activity.id);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'book_saved':
        return <BookOpen className="w-4 h-4 text-primary" />;
      case 'review_created':
      case 'review_updated':
      case 'rating_created':
      case 'rating_updated':
        return <Star className="w-4 h-4 text-star" />;
      case 'level_up':
        return <Trophy className="w-4 h-4 text-yellow-600" />;
      case 'streak_milestone':
        return <Flame className="w-4 h-4 text-orange-500" />;
      case 'dino_duo_completed':
        return <Trophy className="w-4 h-4 text-green-500" />;
      default:
        return <Target className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityMessage = (type: string, payload: any) => {
    switch (type) {
      case 'book_saved':
        return 'added to their TBR';
      case 'review_created':
      case 'rating_created':
        return payload?.review_excerpt ? 'reviewed' : 'rated';
      case 'review_updated':
      case 'rating_updated':
        return payload?.review_excerpt ? 'updated their review for' : 'updated their rating for';
      case 'level_up':
        const level = payload?.new_level || 0;
        const title = payload?.level_title || '';
        return `leveled up to Level ${level} - ${title}!`;
      case 'streak_milestone':
        const days = payload?.streak_days || 0;
        return `achieved a ${days} day reading streak!`;
      case 'dino_duo_completed':
        return 'completed a Dino Duo!';
      default:
        return 'had an activity';
    }
  };

  const getCelebrationBadge = (type: string, payload: any) => {
    if (type === 'book_finished' && payload?.books_finished_count) {
      const count = payload.books_finished_count;
      if (count % 100 === 0) return { text: `🌟 ${count}th book!`, variant: 'default' as const };
      if (count === 50) return { text: '🏆 Half Century!', variant: 'default' as const };
      if (count % 10 === 0) return { text: `🎉 ${count} books!`, variant: 'secondary' as const };
    }
    
    if (type === 'streak_milestone') {
      const days = payload?.streak_days || 0;
      if (days === 100) return { text: '💯 Century Streak!', variant: 'default' as const };
      if (days === 30) return { text: '🔥 Month Streak!', variant: 'default' as const };
      if (days === 7) return { text: '🔥 Week Streak!', variant: 'secondary' as const };
    }
    
    if (type === 'level_up') {
      const level = payload?.new_level || 0;
      if (level === 50) return { text: '👑 Master Level!', variant: 'default' as const };
      if (level === 25) return { text: '⭐ Quarter Century!', variant: 'default' as const };
      if (level % 10 === 0) return { text: `🎊 Level ${level}!`, variant: 'secondary' as const };
    }
    
    return null;
  };

  const celebration = getCelebrationBadge(activity.type, activity.payload);

  return (
    <>
      <Card className="w-full card-elevated overflow-hidden tap-feedback">
        <CardContent className="p-4 sm:p-5">
          <div className="flex gap-3">
            {/* Activity Icon */}
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center">
                {getActivityIcon(activity.type)}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* User info and activity message */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Avatar className="h-6 w-6 flex-shrink-0">
                    <AvatarImage src={activity.profiles?.image} />
                    <AvatarFallback className="text-xs">
                      {activity.profiles?.name?.charAt(0) || activity.profiles?.username?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex items-center gap-1 flex-wrap text-sm min-w-0">
                    <AuthorHeader
                      username={activity.profiles?.username}
                      name={activity.profiles?.name}
                      showFullFormat={false}
                    />
                    <span className="text-muted-foreground">{getActivityMessage(activity.type, activity.payload)}</span>
                  </div>

                  {celebration && (
                    <Badge variant={celebration.variant} className="text-xs">
                      {celebration.text}
                    </Badge>
                  )}
                </div>

                <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                </span>
              </div>

              {/* Book Cover and Title side by side (if applicable) */}
              {activity.book_cover_url && activity.type !== 'level_up' && activity.type !== 'streak_milestone' && (
                <div className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-muted/30 transition-colors tap-feedback cursor-pointer"
                     onClick={() => {
                       const bookId = activity.payload?.book_id;
                       if (bookId) navigate(`/book/${bookId}`);
                     }}>
                  <CoverImage
                    bookId={activity.payload?.book_id || ''}
                    title={activity.book_title || ''}
                    coverUrl={activity.book_cover_url}
                    size="sm"
                  />
                  {activity.book_title && (
                    <span className="font-medium text-sm line-clamp-2">{activity.book_title}</span>
                  )}
                </div>
              )}

              {/* Rating display for review activities */}
              {['review_created', 'review_updated', 'rating_created', 'rating_updated'].includes(activity.type) && 
                activity.payload?.rating_star && (
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.floor(activity.payload.rating_star)
                          ? 'text-star fill-star'
                          : 'text-muted-foreground/30'
                      }`}
                    />
                  ))}
                  <span className="text-sm text-muted-foreground ml-1">
                    {activity.payload.rating_star.toFixed(1)}
                  </span>
                </div>
              )}

              {/* Review excerpt */}
              {['review_created', 'review_updated', 'rating_created', 'rating_updated'].includes(activity.type) && activity.payload?.review_excerpt && (
                <div className="text-sm text-muted-foreground line-clamp-3">
                  {activity.payload.review_excerpt}
                </div>
              )}

              {/* Action Buttons - larger touch targets on mobile */}
              <div className="flex items-center gap-1 pt-1">
                <CheerButton activityId={activity.id} />
                <CommentButton 
                  commentCount={commentCount}
                  onClick={() => setCommentThreadOpen(true)}
                />
                
                {/* Share button for level up milestones */}
                {activity.type === 'level_up' && activity.profiles?.username && (
                  <ExternalShareButton
                    shareType="milestone"
                    data={{
                      count: activity.payload?.new_level || 1,
                      username: activity.profiles.username,
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comment Thread Dialog */}
      <ActivityCommentThread
        isOpen={commentThreadOpen}
        onClose={() => setCommentThreadOpen(false)}
        activityId={activity.id}
        activityDescription={`${activity.profiles?.name || activity.profiles?.username} ${getActivityMessage(activity.type, activity.payload)}${activity.book_title ? ` ${activity.book_title}` : ''}`}
      />
    </>
  );
}